import React, { useEffect, useMemo, useRef, useState } from "react";
import SplitPane from "react-split-pane";
import Pane from "react-split-pane/lib/Pane";
import { useDispatch, useSelector } from "react-redux";
import { isExcel } from "utils";
import Documents from "layout/discovery/folder/documents";
import { showInlineAlert } from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import ContentPanel, {
  getDiscoveryDocItemsArray,
} from "layout/discovery/content/ContentPanel";
import Categories from "layout/discovery/folder/categories";
import { getDiscoveryIgnoreListWithFreq } from "slices/settings/actions/discoveryIgnoreList";

const DiscoveryFolder = () => {
  const themeRef = useRef();
  const docsRef = useRef();
  const nestedSplitPaneRef = useRef();
  const categoriesListRef = useRef();
  const docListRef = useRef();
  const dispatch = useDispatch();

  const [categoriesSize, setCategoriesSize] = useState("20%");
  const [docsListSize, setDocsListSize] = useState("300px");
  const [nestedSplitPaneSize, setNestedSplitPaneSize] = useState("80%");

  const totalOccurrences = useSelector(
    (state) => state.discovery.themes[0]?.Occurrences
  );
  const documents = useSelector(
    (state) => state.discovery.themes[0]?.Documents
  );
  const isEmpty =
    totalOccurrences === 0 &&
    documents?.every(
      (doc) => doc.ignored === undefined || doc.ignored === false
    );
  const allExcel =
    documents &&
    documents.filter((document) => isExcel(document.Name))?.length ===
      documents.length;

  const themes = useSelector((state) => state.discovery.themes);
  const checkedItems = themes?.filter(
    (theme) => theme.checked && theme.NounPhrase !== undefined
  );

  const selectedDocs = useMemo(() => {
    return themes?.[0]?.Documents.filter((doc) => !doc.ignored);
  }, [themes?.[0]?.Documents]);

  const emptyText = () => {
    if (allExcel) {
      return "This folder only contains Excel workbooks. Discovery does not support Excel analysis.";
    }
    if (isEmpty && documents.find((doc) => isExcel(doc.Name))) {
      return "This folder contains only empty docs or Excel Workbooks. Discovery does not work for these types of files.";
    }
    return "This folder contains only empty docs. We can't show any analysis.";
  };

  useEffect(() => {
    if (isEmpty || allExcel) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: emptyText(),
        })
      );
    }
  }, [dispatch, isEmpty, allExcel]);

  const handleDragRelease = () => {
    if (categoriesListRef && categoriesListRef.current) {
      setCategoriesSize(categoriesListRef.current.props.size);
      setNestedSplitPaneSize(
        `${100 - parseFloat(categoriesListRef.current.props.size)}%`
      );
    }

    if (docListRef && docListRef.current) {
      setDocsListSize(docListRef.current.props.size);
    }
  };

  useEffect(() => {
    window.addEventListener("mouseup", handleDragRelease);
    return () => {
      window.removeEventListener("mouseup", handleDragRelease);
    };
  }, []);

  useEffect(() => {
    dispatch(getDiscoveryIgnoreListWithFreq());
  }, [dispatch, selectedDocs]);

  const styles = {
    background: "transparent",
    width: "1px",
    cursor: "col-resize",
    height: "100%",
    paddingRight: "2px",
    borderRight: "transparent 4px solid",
    borderLeft: "transparent 4px solid",
    margin: "0 -5px",
    zIndex: "1",
  };

  return isEmpty || allExcel ? null : (
    <SplitPane
      className="discovery__folder"
      style={{ position: "relative", overflow: "hidden" }}
      split="vertical"
      resizerStyle={styles}
    >
      <Pane
        ref={categoriesListRef}
        minSize="260px"
        initialSize={categoriesSize}
        className="discovery__folder-left"
      >
        <Categories documentsRef={docsRef} categoriesRef={themeRef} />
      </Pane>
      <Pane
        ref={nestedSplitPaneRef}
        minSize="200px"
        className="nested-SplitPane-pane"
        initialSize={nestedSplitPaneSize}
      >
        <SplitPane
          className="nested-SplitPane"
          style={{ position: "relative", overflow: "hidden" }}
        >
          <Pane
            ref={docListRef}
            minSize="200px"
            initialSize={docsListSize}
            className="discovery__folder-middle"
          >
            <Documents docsRef={docsRef} themeRef={themeRef} />
          </Pane>
          <Pane minSize="200px" className="discovery__folder-right">
            <ContentPanel
              docItemsArray={getDiscoveryDocItemsArray(
                selectedDocs,
                checkedItems
              )}
            />
          </Pane>
        </SplitPane>
      </Pane>
    </SplitPane>
  );
};

export default DiscoveryFolder;
